import React from 'react';

import { errorAlert } from '@helpers/Alert';

import type { UserData } from './Card';

const EMPTY_USER_ID_SET: ReadonlySet<string> = new Set();

export type UserListDeletionMutationHandlers = {
  /** Called by the mutation when deleting the user fails. */
  onError: (error: unknown) => void;
  /** Called by the mutation after either success or failure. */
  onSettled: () => void;
  /** Called by the mutation after deleting the user succeeds. */
  onSuccess: () => void;
};

export type UseUserListDeletionOptions<TUser extends UserData> = {
  /** Unique key for the list instance whose local deletion state should be isolated. */
  scopeKey: string;
  /** Optional user array to filter by users that were successfully deleted. */
  users?: readonly TUser[];
  /** Starts the domain-specific delete mutation for the user. */
  onDelete: (user: TUser, handlers: UserListDeletionMutationHandlers) => void;
};

export type UserListDeletion<TUser extends UserData> = {
  deleteUser: (user: TUser) => void;
  deletedUserIds: ReadonlySet<string>;
  deletingUserIds: ReadonlySet<string>;
  isDeleted: (user: TUser) => boolean;
  isDeleting: (user: TUser) => boolean;
  visibleUsers: TUser[];
};

type ScopedUserIds = {
  scopeKey: string;
  userIds: ReadonlySet<string>;
};

export function useUserListDeletion<TUser extends UserData>({
  scopeKey,
  users,
  onDelete,
}: UseUserListDeletionOptions<TUser>): UserListDeletion<TUser> {
  const [deletedUserIdsState, setDeletedUserIdsState] = React.useState<ScopedUserIds>();
  const [deletingUserIdsState, setDeletingUserIdsState] = React.useState<ScopedUserIds>();
  const deletedUserIds = React.useMemo(
    () =>
      deletedUserIdsState?.scopeKey === scopeKey ? deletedUserIdsState.userIds : EMPTY_USER_ID_SET,
    [deletedUserIdsState, scopeKey]
  );
  const deletingUserIds = React.useMemo(
    () =>
      deletingUserIdsState?.scopeKey === scopeKey
        ? deletingUserIdsState.userIds
        : EMPTY_USER_ID_SET,
    [deletingUserIdsState, scopeKey]
  );
  const visibleUsers = React.useMemo(
    () => (users || []).filter((user) => !deletedUserIds.has(user.userId)),
    [deletedUserIds, users]
  );
  const isDeleted = React.useCallback(
    (user: TUser) => deletedUserIds.has(user.userId),
    [deletedUserIds]
  );
  const isDeleting = React.useCallback(
    (user: TUser) => deletingUserIds.has(user.userId),
    [deletingUserIds]
  );
  const deleteUser = React.useCallback(
    (user: TUser) => {
      const userId = user.userId;
      if (deletingUserIds.has(userId)) return;

      function runDelete() {
        setDeletingUserIdsState((currentState) => addScopedUserId(currentState, scopeKey, userId));
        onDelete(user, {
          onError: (error) => errorAlert(error, { onAction: runDelete }),
          onSettled: () => {
            setDeletingUserIdsState((currentState) =>
              removeScopedUserId(currentState, scopeKey, userId)
            );
          },
          onSuccess: () => {
            setDeletedUserIdsState((currentState) =>
              addScopedUserId(currentState, scopeKey, userId)
            );
          },
        });
      }

      runDelete();
    },
    [deletingUserIds, onDelete, scopeKey]
  );

  return {
    deleteUser,
    deletedUserIds,
    deletingUserIds,
    isDeleted,
    isDeleting,
    visibleUsers,
  };
}

function addScopedUserId(
  currentState: ScopedUserIds | undefined,
  scopeKey: string,
  userId: string
): ScopedUserIds {
  const currentUserIds =
    currentState?.scopeKey === scopeKey ? currentState.userIds : new Set<string>();
  const nextUserIds = new Set(currentUserIds);
  nextUserIds.add(userId);

  return { scopeKey, userIds: nextUserIds };
}

function removeScopedUserId(
  currentState: ScopedUserIds | undefined,
  scopeKey: string,
  userId: string
): ScopedUserIds | undefined {
  if (currentState?.scopeKey !== scopeKey) return currentState;

  const nextUserIds = new Set(currentState.userIds);
  nextUserIds.delete(userId);
  if (nextUserIds.size === 0) return undefined;

  return { scopeKey, userIds: nextUserIds };
}
